import {
  DataStatus,
  hasDataError,
  hasDataLoaded,
  isDataLoading,
  isDataNotLoaded,
  isDataSubmitting,
  runSelector,
} from "@app/redux/utils";
import { handleActions } from "redux-actions";
import { handle } from "redux-pack";
import { keyBy, get, map } from "@app/utils/lodash";
import * as API from "@app/API";
import { createSelector } from "reselect";
import { AuthGroup, AuthGroupType } from "@app/entities/auth";
import commonActionTypes from "@app/_Home/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";
import { NOOP } from "@app/utils/helpers";

//#region TYPES
const LOAD_AUTH_GROUPS = "auth-groups/load";
const UPDATE_AUTH_GROUP = "auth-groups/update";
//#endregion

//#region ACTIONS
export const loadAuthGroups = (companyId?: string) => ({
  type: LOAD_AUTH_GROUPS,
  promise: companyId
    ? API.fetchCompanyGroups(companyId)
    : API.fetchMyCompanyGroups(),
  meta: {
    companyId,
  },
});

export const updateAuthGroup = (
  group: AuthGroup,
  onSuccess = NOOP,
  onFailure = NOOP
) => ({
  type: UPDATE_AUTH_GROUP,
  promise: API.updateAuthGroup(group),
  meta: {
    group,
    onSuccess,
    onFailure,
  },
});
//#endregion

//#region SELECTORS
export const getAuthGroupsState = (state) => state.data.authgroups;

export const getAllAuthGroups = createSelector(
  getAuthGroupsState,
  (state) => state.all || []
);

export const getRoles = createSelector(
  getAuthGroupsState,
  (state) => state.roles || []
);

export const getRolesMap = createSelector(getRoles, (roles) => {
  return keyBy(roles, "code");
});

export const getGroups = createSelector(
  getAuthGroupsState,
  (state) => state.groups || []
);

export const getGroupsMap = createSelector(getGroups, (groups) => {
  return keyBy(groups, "code");
});

export const getAuthGroupsStatus = createSelector(
  getAuthGroupsState,
  (state) => state.authGroupStatus
);

export const hasAuthGroupsError = createSelector(
  getAuthGroupsState,
  (status) => status === DataStatus.Error
);

export const isLoadingAuthGroups = createSelector(
  getAuthGroupsState,
  (status) => status === DataStatus.Loading
);

const getResourceKey = createSelector(getAuthGroupsState, (state) => state.key);
//#endregion

export const useAuthGroups = (companyId?: string) => {
  const dispatch = useDispatch();

  const roles = useSelector(getRoles);
  const groups = useSelector(getGroups);
  const rolesMap = useSelector(getRolesMap);
  const groupsMap = useSelector(getGroupsMap);
  const status = useSelector(getAuthGroupsStatus);
  const isLoading = isDataLoading(status);
  const hasError = hasDataError(status);
  const hasLoaded = hasDataLoaded(status);
  const isSubmitting = isDataSubmitting(status);

  const load = useCallback(() => {
    return dispatch(loadAuthGroups(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    const status = runSelector(getAuthGroupsStatus);
    const key = runSelector(getResourceKey);
    if (isDataNotLoaded(status) || key !== companyId) {
      load();
    }
  }, [dispatch, companyId, load]);

  return useMemo(
    () => [
      { roles, rolesMap, groups, groupsMap },
      { isLoading, hasError, hasLoaded, isSubmitting },
      {
        load,
        updateAuthGroup,
      },
    ],
    [
      load,
      roles,
      rolesMap,
      groups,
      groupsMap,
      isLoading,
      hasError,
      hasLoaded,
      isSubmitting,
    ]
  );
};

export interface AuthGroupsState {
  all: AuthGroup[];
  roles: AuthGroup[];
  groups: AuthGroup[];
  authGroupStatus: DataStatus;
  key: string;
}

export const initialState: AuthGroupsState = {
  all: [],
  roles: [],
  groups: [],
  authGroupStatus: DataStatus.NotLoaded,
  key: undefined,
};

export default handleActions(
  {
    [LOAD_AUTH_GROUPS]: (state, action: any) => {
      return handle(state, action, {
        start: (s: AuthGroupsState): AuthGroupsState => ({
          ...s,
          authGroupStatus: DataStatus.Loading,
        }),
        failure: (s: AuthGroupsState): AuthGroupsState => ({
          ...s,
          authGroupStatus: DataStatus.Error,
        }),
        success: (s: AuthGroupsState): AuthGroupsState => {
          const authGroups: AuthGroup[] = get(
            action,
            "payload.data",
            []
          ) as AuthGroup[];

          const roles = authGroups.filter((g) => g.type === AuthGroupType.Role);
          const groups = authGroups.filter(
            (g) => g.type === AuthGroupType.Group
          );

          return {
            ...s,
            all: authGroups,
            roles,
            groups,
            authGroupStatus: DataStatus.Done,
            key: action.meta.companyId,
          };
        },
      });
    },
    [UPDATE_AUTH_GROUP]: (state, action: any) => {
      return handle(state, action, {
        success: (s: AuthGroupsState): AuthGroupsState => {
          const update = get(action, "meta.group", {});
          const roles = s.roles || [];
          return {
            ...s,
            roles: map(roles, (role) =>
              role.code === update.code ? update : role
            ),
          };
        },
      });
    },
    [commonActionTypes.RESET_APP]: () => ({ ...initialState }),
  },
  initialState
);
